/**
 * @file        urgency-analytics.js
 * @description Analytics bridge for the Urgency Messaging feature.
 *
 *              Publish side of the AEP flow: this file turns feature-local
 *              signals into Shopify Web Pixel events so the
 *              `adobe-experience-platform` custom pixel can forward them to
 *              Adobe Experience Platform (ACDL / Launch).
 *
 *              1. Impression — listens for the DOM CustomEvent
 *                 `urgency:message-rendered` dispatched by urgency-renderer.js
 *                 (only fired when a message is actually inserted into the DOM)
 *                 and publishes `urgency_message_displayed`.
 *
 *              2. Skip diagnostics — reads the `#urgency-skip-reasons` JSON
 *                 emitted by urgency-rules.liquid, logs it for troubleshooting,
 *                 and publishes a single `urgency_rule_skipped` event per PDP.
 *
 *              Guards:
 *              - Customer consent is checked before every publish
 *                (Shopify Customer Privacy API). No consent -> nothing fires.
 *              - No PII is attached. Payloads carry only rule / product / variant
 *                identifiers, urgency type, placement, timestamp and the
 *                merchandiser-authored message copy.
 *              - Impressions are de-duplicated per page view on
 *                rule_id + variant_id + placement + fallback_used + message,
 *                so a content change (countdown -> fallback, next delivery slot)
 *                still reports while redundant re-renders do not.
 *
 * @listens     document -> urgency:message-rendered
 * @requires    window.Shopify.analytics.publish
 */
(function () {
  'use strict';

  var IMPRESSION_EVENT = 'urgency_message_displayed';
  var SKIP_EVENT = 'urgency_rule_skipped';

  // rule_id|variant_id|placement|fallback_used|message keys already reported this page view.
  var seenImpressions = new Set();

  function canPublish() {
    return !!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function');
  }

  /**
   * Consent check. Honors the Shopify Customer Privacy API when present
   * (wired by the store's consent-management app). When the API is not on the
   * page at all we fall back to allowing the event — Adobe Launch / Alloy then
   * applies its own server-side consent enforcement.
   */
  function trackingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed();
      }
      if (cp && typeof cp.userCanBeTracked === 'function') {
        return cp.userCanBeTracked();
      }
    } catch (err) {
      // Privacy API present but not ready — be conservative and skip.
      void err;
      return false;
    }
    return true;
  }

  function str(v) {
    return v == null ? '' : String(v);
  }

  function publish(eventName, customData) {
    if (!canPublish()) {
      console.warn('[Urgency Analytics] Shopify.analytics unavailable — ' + eventName + ' not published');
      return;
    }
    if (!trackingAllowed()) {
      return;
    }
    try {
      // Shopify exposes this payload to pixel subscribers as `event.customData`
      window.Shopify.analytics.publish(eventName, customData);
    } catch (e) {
      console.error('[Urgency Analytics] failed to publish ' + eventName, e);
    }
  }

  // ── 1. Impression ─────────────────────────────────────────────
  function onMessageRendered(event) {
    var detail = (event && event.detail) || {};
    var payload = detail.payload || {};
    var product = detail.product || {};

    // Defense in depth: never report an impression for an empty message.
    // urgency-renderer.js already guards this, but keep the analytics layer
    // self-sufficient in case it is ever fed a rendered event directly.
    if (str(payload.message).trim() === '') return;

    var ruleId = str(payload.rule_id);
    var productId = str(product.id || payload.product_id);
    // urgency-init.js keeps product.variant_id in sync with the selected variant.
    var variantId = str(product.variant_id || payload.variant_id);
    var placement = str(payload.placement);
    var messageText = str(payload.message);
    var fallbackUsed = !!payload.fallback_used;

    var dedupeKey = ruleId + '|' + variantId + '|' + placement + '|' + fallbackUsed + '|' + messageText;
    if (seenImpressions.has(dedupeKey)) return;
    seenImpressions.add(dedupeKey);

    publish(IMPRESSION_EVENT, {
      ruleId: ruleId,
      ruleName: str(payload.rule_name),
      urgencyType: str(payload.urgency_type),
      placement: placement,
      slotSelector: str(detail.slotSelector),
      productId: productId,
      variantId: variantId,
      messageText: messageText,
      fallbackUsed: fallbackUsed,
      hasCountdown: !!payload.countdown_deadline,
      countdownDeadline: payload.countdown_deadline || null,
      timestamp: detail.timestamp || new Date().toISOString(),
    });
  }

  // ── 2. Skip diagnostics ───────────────────────────────────────
  function reportSkips() {
    var el = document.getElementById('urgency-skip-reasons');
    if (!el) return;

    var skips;
    try {
      skips = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[Urgency Analytics] could not parse #urgency-skip-reasons', e);
      return;
    }
    if (!Array.isArray(skips) || skips.length === 0) return;

    var productEl = document.getElementById('urgency-product-data');
    var productId = '';
    var variantId = '';
    try {
      var p = JSON.parse(productEl.textContent);
      productId = str(p.id);
      variantId = str(p.variant_id);
    } catch (err) {
      /* product context is best-effort */
      void err;
    }

    try {
      var variantInput = document.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        variantId = str(variantInput.value);
      }
    } catch (err2) {
      void err2;
    }

    publish(SKIP_EVENT, {
      productId: productId,
      variantId: variantId,
      skippedCount: skips.length,
      skips: skips.map(function (s) {
        return {
          ruleId: str(s.rule_id),
          ruleName: str(s.rule_name),
          urgencyType: str(s.urgency_type),
          placement: str(s.placement),
          priority: s.priority == null ? null : s.priority,
          reason: str(s.reason),
        };
      }),
      timestamp: new Date().toISOString(),
    });
  }

  document.addEventListener('urgency:message-rendered', onMessageRendered);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportSkips);
  } else {
    reportSkips();
  }
})();
